class ColorVariantCardHandler {
  constructor() {
    this.observer = null;
    this._reprocessTimeout = null;
    this._applyingSwap = false; // guards against reacting to our own DOM writes
    this.init();
  }

  init() {
    this.processAllCards();
    this.observeGrid();
  }

  processAllCards() {
    const cards = Array.from(document.querySelectorAll('product-card')).filter((card) =>
      card.querySelector('.product-card__swatch-group')
    );
    if (!cards.length) return;

    this._applyingSwap = true;

    cards.forEach((card) => this.setupCardSwatches(card));

    // release the guard after our own DOM writes (image src, slide hidden attrs, etc.)
    // have already produced their mutation records
    requestAnimationFrame(() => {
      this._applyingSwap = false;
    });
  }

  /**
   * Watches for sort/filter/pagination re-renders (Shopify morph). New cards
   * get added, or existing cards' slideshow children get reset — either way
   * we need to (re)bind swatch listeners and restore the default preview.
   */
  observeGrid() {
    if (this.observer) return;

    const grid = document.querySelector('.product-grid-container') || document.body;

    this.observer = new MutationObserver(() => {
      if (this._applyingSwap) return; // ignore mutations we caused ourselves

      clearTimeout(this._reprocessTimeout);
      this._reprocessTimeout = setTimeout(() => this.processAllCards(), 50);
    });

    this.observer.observe(grid, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['hidden', 'aria-hidden', 'initial-slide'],
    });
  }

  setupCardSwatches(productCard) {
    const swatchGroup = productCard.querySelector('.product-card__swatch-group');
    if (!swatchGroup) return;

    const swatches = swatchGroup.querySelectorAll('.product-card__swatch');
    if (!swatches.length) return;

    swatches.forEach((swatch) => {
      // preview whatever is currently the "active" (checked) swatch by default
      if (!swatch.dataset.swatchBound) {
        swatch.dataset.swatchBound = 'true';

        swatch.addEventListener('mouseenter', () => this.previewSwatch(productCard, swatch));
        swatch.addEventListener('focus', () => this.previewSwatch(productCard, swatch));
        swatch.addEventListener('mouseleave', () => this.resetSwatchPreview(productCard, swatchGroup));
        swatch.addEventListener('blur', () => this.resetSwatchPreview(productCard, swatchGroup));

        swatch.addEventListener(
          'click',
          (e) => {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            this.navigateToVariant(swatch);
          },
          true
        );
      }
    });

    // set the initial image state to match whichever swatch is checked
    this.resetSwatchPreview(productCard, swatchGroup);
  }

  // ─── Preview (hover) ─────────────────────────────────────────────────────────

  previewSwatch(productCard, swatch) {
    const { featuredMediaId, hoverMediaId, variantMediaSrc } = swatch.dataset;
    if (!featuredMediaId && !variantMediaSrc) return;

    let imageSwapped = false;

    if (featuredMediaId) {
      imageSwapped = this.trySlideShowSwap(productCard, featuredMediaId, hoverMediaId);
    }

    if (!imageSwapped && variantMediaSrc) {
      this.swapImagesFromSrc(productCard, variantMediaSrc);
    }
  }

  resetSwatchPreview(productCard, swatchGroup) {
    const checkedInput = swatchGroup.querySelector('.product-card__swatch-input:checked');
    const activeLabel = checkedInput
      ? swatchGroup.querySelector(`label[for="${checkedInput.id}"]`)
      : swatchGroup.querySelector('.product-card__swatch');

    if (activeLabel) {
      this.previewSwatch(productCard, activeLabel);
    }
  }

  // ─── Slideshow Strategy ──────────────────────────────────────────────────────

  /**
   * Keeps the featured slide AND the hover slide visible for this color,
   * hides everything else, so <product-card>'s own previewImage()/resetImage()
   * hover logic (and our initial preview call) can find the right slide.
   */
  trySlideShowSwap(productCard, featuredMediaId, hoverMediaId) {
    const slideshow = productCard.querySelector('slideshow-component');
    if (!slideshow) return false;

    const slides = slideshow.querySelectorAll('slideshow-slide');
    if (!slides.length) return false;

    let targetIndex = -1;
    const keepVisibleIds = new Set([featuredMediaId, hoverMediaId].filter(Boolean).map(String));

    slides.forEach((slide, index) => {
      const slideId = String(slide.getAttribute('slide-id'));

      if (slideId === String(featuredMediaId)) {
        targetIndex = index;
      }

      if (keepVisibleIds.has(slideId)) {
        slide.removeAttribute('aria-hidden');
        slide.removeAttribute('hidden');
      } else {
        slide.setAttribute('aria-hidden', 'true');
        slide.setAttribute('hidden', '');
      }
    });

    if (targetIndex === -1) return false;

    slideshow.setAttribute('initial-slide', String(targetIndex));

    // Intentionally no scrollIntoView() here — initial-slide handles the
    // slideshow's own positioning; scrollIntoView bubbles up to the page's
    // scroll container inside a horizontal carousel and yanks page scroll.

    return true;
  }

  // ─── Direct Src Swap (fallback when there's no slideshow-component) ──────────

  swapImagesFromSrc(productCard, variantMediaSrc) {
    const swapNow = () => {
      const imgs = productCard.querySelectorAll('img[src*="cdn.shopify.com"]');
      imgs.forEach((img) => {
        const width = img.getAttribute('width') || img.offsetWidth || 600;
        img.src = this.shopifySizedUrl(variantMediaSrc, width);
        if (img.hasAttribute('srcset')) {
          img.srcset = this.shopifySrcset(variantMediaSrc);
        }
      });

      const sources = productCard.querySelectorAll('source[srcset*="cdn.shopify.com"]');
      sources.forEach((source) => {
        source.srcset = this.shopifySrcset(variantMediaSrc);
      });
    };

    swapNow();
    requestAnimationFrame(swapNow);
  }

  shopifySizedUrl(src, width) {
    return src.replace(/_\d+x\d*(\.[a-z]+)/i, '$1').replace(/(\.[a-z]+)(\?)/i, `_${width}x$1`);
  }

  shopifySrcset(src) {
    const widths = [200, 400, 600, 800, 1000, 1200, 1600];
    return widths.map((w) => `${this.shopifySizedUrl(src, w)} ${w}w`).join(', ');
  }

  // ─── Click → PDP Redirect ──────────────────────────────────────────────────

  navigateToVariant(swatch) {
    const { variantUrl, variantId } = swatch.dataset;

    if (variantUrl) {
      window.location.href = variantUrl;
      return;
    }

    if (variantId) {
      const productCard = swatch.closest('product-card');
      const link = productCard?.querySelector('a[href*="/products/"]');
      if (link) {
        const url = new URL(link.getAttribute('href'), window.location.origin);
        url.searchParams.set('variant', variantId);
        window.location.href = url.pathname + url.search;
      }
    }
  }
}

// ─── Init ────────────────────────────────────────────────────────────────────

let handlerInstance = null;

function initColorVariantCards() {
  if (handlerInstance) return;
  handlerInstance = new ColorVariantCardHandler();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initColorVariantCards);
} else {
  initColorVariantCards();
}

document.addEventListener('shopify:section:load', () => {
  if (handlerInstance?.observer) {
    handlerInstance.observer.disconnect();
  }
  handlerInstance = null;
  initColorVariantCards();
});